import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// --------------------------------------------------------------------------- //
// GeoSplash — first-visit locale + market picker.
// Renders BEFORE anything else in App.js. Persists the parent's choice in
// localStorage.gop_locale_chosen so subsequent visits skip the modal.
// --------------------------------------------------------------------------- //

const HEADLINE_BY_UI_LANG = {
  en: 'Choose your country',
  si: 'ඔබේ රට තෝරන්න',
  ta: 'உங்கள் நாட்டைத் தேர்ந்தெடுக்கவும்',
  hi: 'अपना देश चुनें',
  ms: 'Pilih negara anda',
  bn: 'আপনার দেশ বেছে নিন',
};

function detectUiLang() {
  try {
    const nav = (navigator.language || 'en').toLowerCase();
    if (nav.startsWith('si')) return 'si';
    if (nav.startsWith('ta')) return 'ta';
    if (nav.startsWith('hi')) return 'hi';
    if (nav.startsWith('ms')) return 'ms';
    if (nav.startsWith('bn')) return 'bn';
  } catch (e) { /* ignore */ }
  return 'en';
}

const FlagBtn = ({ flag, label, sub, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="w-full flex flex-col items-center gap-2 p-6 rounded-2xl border-2 border-transparent bg-white hover:border-primary hover:shadow-xl transition text-left"
  >
    <span className="text-6xl" aria-hidden>{flag}</span>
    <span className="text-lg font-semibold text-gray-900">{label}</span>
    {sub && <span className="text-xs text-gray-500 text-center">{sub}</span>}
  </button>
);

export default function GeoSplash() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [expandIndia, setExpandIndia] = useState(false);
  const [detectedLang, setDetectedLang] = useState('en');

  useEffect(() => {
    // Skip on friction-free public share routes (free sample lesson at /try,
    // waitlist pages, and parent PIN gate). A parent tapping a WhatsApp share
    // link should not be asked to pick a country first.
    try {
      const p = (window.location.pathname || '').toLowerCase();
      const waitlistPaths = new Set(['/try', '/tn', '/bd', '/my', '/in-hi', '/id', '/th', '/pk', '/hk']);
      const bypass = waitlistPaths.has(p) || p.startsWith('/parent/') || p.startsWith('/verify-cert/') || p.startsWith('/admin');
      if (bypass) return;
    } catch (e) { /* ignore */ }
    try {
      const chosen = localStorage.getItem('gop_locale_chosen');
      if (chosen) return;                       // already picked → skip
    } catch (e) { /* localStorage blocked → still show */ }
    const dl = detectUiLang();
    setDetectedLang(dl);
    setVisible(true);
  }, []);

  const pick = (locale, route) => {
    try {
      localStorage.setItem('gop_locale_chosen', locale);
      localStorage.setItem('i18nextLng', locale);
    } catch (e) { /* ignore */ }
    i18n.changeLanguage(locale);
    setVisible(false);
    if (route && route !== window.location.pathname) navigate(route);
  };

  if (!visible) return null;

  const headline = HEADLINE_BY_UI_LANG[detectedLang] || HEADLINE_BY_UI_LANG.en;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="geo-splash"
      className="fixed inset-0 z-[1000] bg-gradient-to-br from-slate-900/95 via-emerald-950/95 to-slate-900/95 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="w-full max-w-4xl bg-gradient-to-br from-amber-50 via-white to-emerald-50 rounded-3xl p-8 shadow-2xl">
        <p className="text-xs uppercase tracking-widest text-emerald-700 mb-2">Genius One Plus</p>
        <h2 className="text-3xl md:text-4xl font-bold mb-2" data-testid="geo-splash-headline">
          {headline}
        </h2>
        <p className="text-sm text-gray-600 mb-8">
          Pick where you are and we&rsquo;ll set the right language for you.
        </p>

        {!expandIndia ? (
          <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="geo-primary-grid">
            <FlagBtn flag="🇱🇰" label="Sri Lanka" sub="ශ්‍රී ලංකා · இலங்கை"
                     testId="geo-lk"
                     onClick={() => pick('si-en-dual', '/')} />
            <FlagBtn flag="🇮🇳" label="India" sub="भारत · Choose language →"
                     testId="geo-in"
                     onClick={() => setExpandIndia(true)} />
            <FlagBtn flag="🇲🇾" label="Malaysia" sub="Bahasa Melayu · Coming 2027"
                     testId="geo-my"
                     onClick={() => pick('ms', '/my')} />
            <FlagBtn flag="🇧🇩" label="Bangladesh" sub="বাংলাদেশ"
                     testId="geo-bd"
                     onClick={() => pick('bn', '/bd')} />
          </div>
          {/* Sprint 10.9 — Additional expansion markets (waitlist-only, Coming 2027) */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="geo-expansion-grid">
            <FlagBtn flag="🇮🇩" label="Indonesia" sub="Coming 2027"
                     testId="geo-id"
                     onClick={() => pick('en', '/id')} />
            <FlagBtn flag="🇹🇭" label="Thailand" sub="Coming 2027"
                     testId="geo-th"
                     onClick={() => pick('en', '/th')} />
            <FlagBtn flag="🇵🇰" label="Pakistan" sub="Coming 2027"
                     testId="geo-pk"
                     onClick={() => pick('en', '/pk')} />
            <FlagBtn flag="🇭🇰" label="Hong Kong" sub="Coming 2027 · Diaspora"
                     testId="geo-hk"
                     onClick={() => pick('en', '/hk')} />
            <FlagBtn flag="🌏" label="Somewhere else" sub="Explore in English"
                     testId="geo-elsewhere"
                     onClick={() => pick('en', '/')} />
          </div>
          </>
        ) : (
          <div>
            <p className="text-sm text-gray-700 mb-4" data-testid="geo-india-headline">
              🇮🇳 India — pick your region
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <FlagBtn flag="🇮🇳" label="Hindi belt (Delhi / UP / Bihar / MP)"
                       sub="हिन्दी · Coming 2027 waitlist"
                       testId="geo-in-hi"
                       onClick={() => pick('hi', '/in-hi')} />
              <FlagBtn flag="🇮🇳" label="Tamil Nadu preview"
                       sub="தமிழ் · Chennai / Coimbatore / Madurai"
                       testId="geo-in-tn"
                       onClick={() => pick('ta', '/tn')} />
            </div>
            <button
              onClick={() => setExpandIndia(false)}
              className="text-xs text-gray-500 underline"
              data-testid="geo-india-back"
            >
              ← Back
            </button>
          </div>
        )}

        <div className="mt-8 text-center">
          <button
            className="text-xs text-gray-500 hover:text-gray-700 underline"
            data-testid="geo-english"
            onClick={() => pick('en', '/')}
          >
            Or explore in English
          </button>
        </div>
      </div>
    </div>
  );
}
